<div class="sample-planning-detail">
  <!-- DASH-3190 (item 1): the plan-detail page is a DETAIL view reached from the Planning list, so it renders NO
  app-tab-main bar — matching its peer, the Risk Assessment detail page (risk-register), which renders none. The
  tab bar belongs on the list pages. This reverses DASH-2718 AC6 and removes the DASH-2736 offset workaround
  (.main-container{bottom:20px} + .content{margin-top:-80px}) that existed only to fight app-tab-main's
  hard-coded negative offsets. -->
  <!-- DASH-2757 (Issue 1): the plan name is the page's primary identifier, so it renders as the detail-page title on
  its own line above the action row (not as a form control competing with the matrix Save). While the plan is
  unlocked (Draft/Rejected) and the user has Update permission it is editable in place: a pencil affordance swaps
  the title for an inline input committed with a check icon-button (or Enter) and reverted with a cancel
  icon-button (or Esc). Locked / no-permission plans show a static title. -->
  @if (plan) {
    <div class="plan-title-row">
      <div class="plan-title-left">
        <span
          class="back-btn cursor-pointer"
          (click)="goBack()"
          [attr.aria-label]="'Back to Sampling Plans' | translate"
          [title]="'Back to Sampling Plans' | translate">
          <span class="material-symbols-outlined purple-text">chevron_left</span>
        </span>
        @if (!isEditingName) {
          <h4 class="plan-title card-title mb-0" [title]="planName">{{ planName | nullDisplay }}</h4>
          @if (canEditName) {
            <button
              type="button"
              class="plan-title-edit"
              (click)="startEditName()"
              [attr.aria-label]="'Edit plan name' | translate"
              [title]="'Edit plan name' | translate">
              <i class="material-icons">edit</i>
            </button>
          }
        } @else {
          <label class="sr-only" for="planNameInput">{{ 'Plan Name' | translate }}</label>
          <input
            id="planNameInput"
            type="text"
            class="plan-name-input"
            maxlength="200"
            [(ngModel)]="nameDraft"
            [disabled]="isSavingName"
            (keyup.enter)="saveName()"
            (keyup.escape)="cancelEditName()"
            [attr.aria-invalid]="!hasValidName"
            [attr.aria-describedby]="!hasValidName ? 'planNameError' : null" />
            <button
              type="button"
              class="plan-name-action confirm"
              [disabled]="!hasNameChange || isSavingName"
              (click)="saveName()"
              [attr.aria-label]="'Save plan name' | translate"
              [title]="'Save plan name' | translate">
              <i class="material-icons">check</i>
            </button>
            <button
              type="button"
              class="plan-name-action cancel"
              [disabled]="isSavingName"
              (click)="cancelEditName()"
              [attr.aria-label]="'Cancel' | translate"
              [title]="'Cancel' | translate">
              <i class="material-icons">close</i>
            </button>
            @if (!hasValidName) {
              <span id="planNameError" class="plan-name-error" role="alert">
                {{ 'Plan name is required' | translate }}
              </span>
            }
          }
        </div>
        @if (matrix) {
          <div class="risk-meta-strip">
            <div class="meta-item">
              <span class="meta-label">{{ 'Status' | translate }}</span>
              <span class="meta-value">{{ SamplingPlanStatus[matrix.status] | translate }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">{{ 'Year' | translate }}</span>
              <span class="meta-value">{{ matrix.year | nullDisplay }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">{{ 'Site' | translate }}</span>
              <span class="meta-value" [title]="matrix.siteName">{{ matrix.siteName | nullDisplay }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">{{ 'Reference Risk Assessment' | translate }}</span>
              <span class="meta-value" [title]="referenceRaLabel">
                {{ showReferenceRaName ? referenceRaName : (referenceRaLabel | translate) }}
              </span>
            </div>
            @if (hasReferencePeriod) {
              <div class="meta-item">
                <span class="meta-label">{{ 'Time Period' | translate }}</span>
                <span class="meta-value">
                  {{ matrix.referenceRiskAssessmentPeriodStart | dateFormat }} -
                  {{ matrix.referenceRiskAssessmentPeriodEnd | dateFormat }}
                </span>
              </div>
            }
          </div>
        }
      </div>
    }

    @if (matrix) {
      <div class="detail-toolbar">
        <div class="toolbar-left">
          <div class="search-bar">
            <div class="search-input-div d-flex">
              <div class="input-group">
                <div class="input-field">
                  <input
                    type="search"
                    class="search-input mb-0"
                    [(ngModel)]="searchTerm"
                    placeholder="{{ 'Search agents' | translate }}" />
                  </div>
                  <button type="button" class="input-group-addon">
                    <label class="label-icon">
                      <i class="material-icons mb-0">search</i>
                    </label>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="toolbar-right">
            <div class="risk-coloring-toggle">
              <span class="toggle-label" [ngClass]="{ active: !useHealthRiskColoring }">
                {{ 'Likelihood View' | translate }}
              </span>
              <mat-slide-toggle
                [ngModel]="useHealthRiskColoring"
                (change)="toggleColoringMode()"
                class="coloring-mode-toggle">
              </mat-slide-toggle>
              <span class="toggle-label" [ngClass]="{ active: useHealthRiskColoring }">
                {{ 'Health Risk View' | translate }}
              </span>
            </div>
            @if (!isLocked) {
              <button
                type="button"
                class="btn bulk-btn"
                [class.active]="bulkMode"
                (click)="toggleBulkMode()">
                {{ (bulkMode ? 'Exit Bulk' : 'Go Bulk') | translate }}
              </button>
            }
            <button
              type="button"
              class="btn save-btn"
              [disabled]="isLocked || !hasChanges || isSaving"
              (click)="save()">
              {{ 'Save' | translate }}
            </button>
            <!-- DASH-2742 (G11v2 approve): Approve is hidden (not merely disabled) once the plan is Approved, as with
            Go Bulk; Revert to Draft takes its place so an approved plan is recoverable. -->
            @if (canApprove) {
              <button
                type="button"
                class="btn approve-btn"
                (click)="approve()">
                {{ 'Approve' | translate }}
              </button>
            }
            @if (canRevert) {
              <button
                type="button"
                class="btn revert-btn"
                (click)="revertToDraft()">
                {{ 'Revert to Draft' | translate }}
              </button>
            }
            <div class="filters" (click)="$event.stopPropagation()">
              <button type="button" class="btn filters-btn" [class.active]="filtersOpen" (click)="toggleFiltersPopup()">
                <i class="material-icons">filter_list</i>
                <span>{{ 'Filters' | translate }}</span>
              </button>
              @if (filtersOpen) {
                <div class="filters-popup">
                  @for (option of filterOptions; track trackByIndex($index)) {
                    <label class="filter-option">
                      <mat-checkbox
                        color="primary"
                        [checked]="isFilterActive(option.id)"
                        (change)="toggleFilter(option.id)">
                        {{ option.label | translate }}
                      </mat-checkbox>
                    </label>
                  }
                  <button type="button" class="btn clear-filters-btn" (click)="clearFilters()">
                    {{ 'Clear filters' | translate }}
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      }

      @if (matrix && isLocked) {
        <div class="lock-banner">
          <span class="material-icons">lock</span>
          <span>{{ 'This sampling plan is approved and locked. The matrix is read-only.' | translate }}</span>
        </div>
      }

      @if (matrix && !hasReferenceRa) {
        <div class="no-reference">
          {{ 'This plan has no Reference Risk Assessment — all cells are shown as Not assessed.' | translate }}
        </div>
      }

      @if (appliedFilterChips.length) {
        <div class="active-filters">
          @for (chip of appliedFilterChips; track trackByIndex($index)) {
            <div class="filter-chip">
              <span>{{ chip.label | translate }}</span>
              <button type="button" class="remove-chip" (click)="removeFilter(chip.id)" [title]="'Remove filter' | translate">
                <i class="material-icons">close</i>
              </button>
            </div>
          }
        </div>
      }

      @if (bulkMode) {
        <div class="bulk-bar">
          <span class="bulk-count">{{ selectedCount }} {{ 'selected' | translate }}</span>
          <input
            type="number"
            min="0"
            class="bulk-input"
            [(ngModel)]="bulkValue"
            placeholder="{{ 'Planned #' | translate }}" />
            <button type="button" class="btn apply-bulk-btn" [disabled]="!selectedCount" (click)="applyBulk()">
              {{ 'Apply to selected' | translate }}
            </button>
          </div>
        }

        @if (isLoading) {
          <div class="loading">{{ 'Loading...' | translate }}</div>
        }

        <!-- DASH-2718 item 1 / DASH-3190 (item 5): .custom-theme-table is the inner scroll container (body has global
        overflow: hidden). The host .sample-planning-detail is now a fixed-height flex column, so this container flexes
        to fill the space below the header/toolbar and scrolls both axes itself — no viewport-height magic number. The
        matrix's horizontal scrollbar therefore sits at the container's (viewport) bottom and stays reachable without
        scrolling the page, and the SEG Profile Summary (moved inside) shares this one vertical scroll. -->
        @if (matrix && !isLoading) {
          <div class="custom-theme-table">
            <div class="custom-scrollbar sticky-header table-shadow">
              <table class="table table-striped matrix-table">
                <thead>
                  <tr>
                    <th class="sticky-col left-aligned">{{ 'SEG' | translate }}</th>
                    @for (column of visibleColumns; track trackByIndex(i); let i = $index) {
                      <th
                        class="agent-col"
                        [title]="column.isGroup ? '' : column.name">
                        <div class="col-head">
                          @if (bulkMode) {
                            <mat-checkbox
                              class="head-checkbox"
                              color="primary"
                              [checked]="columnSelected[i]"
                              (change)="toggleColumnSelect(i)">
                            </mat-checkbox>
                          }
                          <div class="col-labels">
                            <span class="col-name">{{ column.name }}</span>
                            @if (column.isGroup) {
                              <span class="group-hint">
                                {{ 'Group' | translate }}
                              </span>
                            }
                          </div>
                        </div>
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of viewRows; track trackByIndex($index)) {
                    <tr>
                      <td class="sticky-col left-aligned seg-name">
                        <div class="seg-head">
                          @if (bulkMode) {
                            <mat-checkbox
                              class="head-checkbox"
                              color="primary"
                              [checked]="row.selected"
                              (change)="toggleRowSelect(row)">
                            </mat-checkbox>
                          }
                          <span [title]="row.seg.segName">{{ row.seg.segName }}</span>
                        </div>
                      </td>
                      @for (vm of row.cells; track trackByIndex($index)) {
                        <td
                          class="matrix-cell"
                          [class.not-assessed]="vm.notAssessed"
                          [class.hazard-na]="vm.isHazardNa"
                          [class.filtered-out]="vm.filteredOut"
                          [class.selected]="vm.selected"
                          [title]="vm.isRollup ? vm.rollupTitle : null">
                          <div class="analyte-cell">
                            <div class="left-block" [ngStyle]="{ 'background-color': vm.color }">
                              <div class="cell-indicators">
                                @if (bulkMode && vm.plannable && !vm.filteredOut) {
                                  <mat-checkbox
                                    class="cell-checkbox"
                                    color="primary"
                                    [checked]="vm.selected"
                                    (change)="toggleCellSelect(vm)">
                                  </mat-checkbox>
                                }
                                @if (vm.isQuant) {
                                  <span
                                    class="quant-image-badge"
                                    [title]="'Quantitative' | translate">
                                    <img class="quant-image" src="assets/images/quant-icon.svg" alt="bars">
                                  </span>
                                }
                                @if (vm.isQual) {
                                  <span
                                    class="bordered-span quant-qual-label"
                                  [title]="'Qualitative' | translate">{{ 'QUAL' | translate }}</span>
                                }
                                @if (vm.isOverridden) {
                                  <span
                                    class="override-marker"
                                  [title]="'Override' | translate">{{ 'Override' | translate }}</span>
                                }
                              </div>
                              @if (vm.isHazardNa) {
                                <span class="hazard-label">{{ 'N/A' | translate }}</span>
                                @if (vm.showEnable) {
                                  <button
                                    type="button"
                                    class="enable-btn"
                                    (click)="onEnableOverride(vm)">
                                    {{ 'Enable' | translate }}
                                  </button>
                                }
                              } @else {
                                <div class="cell-body">
                                  <span
                                    class="band-label"
                                    [class.band-label--none]="!vm.bandLabel"
                                    [title]="vm.bandLabel || ('Not assessed' | translate)">
                                    {{ vm.bandLabel || ('Not assessed' | translate) }}
                                  </span>
                                  <input
                                    type="number"
                                    min="0"
                                    class="planned-input"
                                    [disabled]="!vm.plannable"
                                    [ngModel]="vm.planned"
                                    (ngModelChange)="onPlannedChange(vm, $event)"
                                    [title]="(vm.isGroupCol ? 'Direct group samples' : (vm.isRollup ? 'Direct planned samples' : 'Planned number of samples')) | translate" />
                                    @if (vm.isRollup) {
                                      <div class="group-rollup" [title]="vm.rollupTitle">
                                        <span class="contribution-chip" [title]="'Samples inherited from agent groups' | translate">
                                          <i class="material-icons">lock</i>{{ vm.contribution }}
                                        </span>
                                        <span class="total-chip" [title]="'Total planned samples' | translate">
                                          {{ 'Total' | translate }}: {{ vm.total }}
                                        </span>
                                      </div>
                                    }
                                  </div>
                                }
                              </div><!-- .left-block -->
                            </div><!-- .analyte-cell -->
                          </td>
                        }
                      </tr>
                    }
                    @if (!viewRows.length) {
                      <tr>
                        <td class="sticky-col left-aligned" [attr.colspan]="visibleColumns.length + 1">
                          {{ 'No SEGs to plan for this plan.' | translate }}
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
                <!-- DASH-2712 AC5 / DASH-3190 (item 5): the SEG Profile Summary lives INSIDE the matrix scroll container so the
                page has exactly ONE vertical scroll region — matrix + summary share it — instead of forcing a
                document-level scroll that tore the fixed app shell (detached header, sheared grid). Per column, the
                Planned rollup across all SEGs. An AGENT row shows its DIRECT total + the group-agnostic
                'incl. N from group(s)' annotation (per-group names on hover only) and a computed TOTAL; a GROUP row stays a
                SEPARATE line carrying only its own DIRECT total (never summed into a member agent's row). -->
                @if (matrix && !isLoading && plannedRollups.length) {
                  <div class="seg-profile-summary">
                    <h3 class="summary-title">{{ 'SEG Profile Summary' | translate }}</h3>
                    <table class="table summary-table">
                      <thead>
                        <tr>
                          <th class="left-aligned">{{ 'Agent' | translate }}</th>
                          <th>{{ 'Direct' | translate }}</th>
                          <th>{{ 'From Groups' | translate }}</th>
                          <th>{{ 'Planned Total' | translate }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (rollup of plannedRollups; track trackByIndex($index)) {
                          <tr
                            class="summary-row"
                            [class.group-row]="rollup.isGroup">
                            <td class="left-aligned summary-name">
                              <span>{{ rollup.columnName }}</span>
                              @if (rollup.isGroup) {
                                <span class="group-hint">{{ 'Group' | translate }}</span>
                              }
                            </td>
                            <td class="summary-direct">{{ rollup.direct }}</td>
                            <td class="summary-contribution">
                              @if (!rollup.isGroup && rollup.contribution) {
                                <span
                                  class="contribution-note"
                                  [title]="rollup.rollupTitle">
                                  {{ rollup.contributionLabel }}
                                </span>
                              } @else {
                                <span class="contribution-none">—</span>
                              }
                            </td>
                            <td class="summary-total">{{ rollup.total }}</td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                  }<!-- .seg-profile-summary -->
                </div><!-- .custom-scrollbar.sticky-header -->
              </div>
              }<!-- .custom-theme-table -->
            </div>
